import { SafeParseError, SafeParseSuccess, undefined, ZodIssue } from 'zod';

import { OptionItemObject } from '../../../shared_models/interfaces';
import { ValidationResult } from '../model';
import { ImportExportJson, ImportExportLevel, ImportExportOption } from '../public_model';

import { getImportExportJsonZod, ImportExportJsonZodType } from './ImportExportJson.zod';
import { getInvalidValue, Validator } from './Validator';

class JsonValidator implements Validator<ImportExportJson, string, ImportExportJsonZodType> {
    getStatistics(json: ImportExportJson): { toAdd: number; toUpdate: number } {
        return {
            toAdd: filterNodesAndCount(json.firstLevel, opt => !opt.id),
            toUpdate: filterNodesAndCount(json.firstLevel, opt => !!opt.id),
        };
    }

    isSuccess(
        validationResult: SafeParseError<ImportExportJsonZodType> | SafeParseSuccess<ImportExportJsonZodType>
    ): validationResult is SafeParseSuccess<ImportExportJsonZodType> {
        return validationResult.success;
    }

    validate(input: string, treeData: OptionItemObject[]): ValidationResult {
        try {
            const json = JSON.parse(input) as ImportExportJson;
            const result = getImportExportJsonZod(treeData).safeParse(json);
            const statistics = this.getStatistics(json);
            return { ...this.convertToReadableString(result), statistics };
        } catch (e) {
            // @ts-ignore
            return { success: false, errors: [{ message: e.message, path: '', invalidValue: '' }] };
        }
    }

    private convertToReadableString(
        validationResult: SafeParseError<ImportExportJsonZodType> | SafeParseSuccess<ImportExportJsonZodType>
    ) {
        if (this.isSuccess(validationResult)) {
            return { success: true, errors: [] };
        } else {
            const errors = validationResult.error.issues.map(item => ({
                message: item.message,
                path: convertToStringPath(item.path, item),
                invalidValue: getInvalidValue(item),
            }));
            return { success: false, errors };
        }
    }
}

const filterNodesAndCount = (element: ImportExportLevel, filter: (opt: ImportExportOption) => boolean) => {
    let count = 0;
    // @ts-ignore
    if (element.options === undefined) return 0;
    element?.options?.forEach(function (c) {
        if (c.nestedLevel) {
            const r = filterNodesAndCount(c.nestedLevel, filter);
            count += r;
        }
    });
    const filtered = element?.options?.filter(filter).length || 0;
    return filtered + count;
};

const convertToStringPath = (path: (string | number)[], issue: ZodIssue): string => {
    const rowLen = path.length;
    const isLast = (i: number, rowLen: number) => rowLen === i + 1;
    return path
        .map((item, i) => {
            if (typeof item === 'number') {
                return `${item}.`;
            } else {
                return isLast(i, rowLen) ? `${item}` : `${item}.`;
            }
        })
        .join('');
};

const jsonValidator = new JsonValidator();
export default jsonValidator;
